import { EventTemplate, finalizeEvent, NostrEvent } from "nostr-tools";
import { encrypt as nip04Encrypt, decrypt as nip04Decrypt } from "nostr-tools/nip04";
import { getPublicKey } from "nostr-tools/pure";
import { hexToBytes, bytesToHex } from "@noble/hashes/utils";
import { firstValueFrom, filter, timeout as rxTimeout, map } from "rxjs";

import pool from "./pool";
import { recordTiming } from "./telemetry";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NwcConnection {
  walletPubkey: string; // wallet service pubkey (hex, from URI)
  relay: string; // relay URL (from URI)
  clientSecret: Uint8Array; // client secret key (from URI)
  clientPubkey: string; // derived from clientSecret
}

export interface NwcTransaction {
  type: "incoming" | "outgoing";
  invoice: string;
  description: string;
  description_hash: string;
  preimage: string;
  payment_hash: string;
  amount: number; // msats
  fees_paid: number; // msats
  created_at: number; // unix timestamp
  settled_at?: number;
}

interface NwcResponse {
  result_type: string;
  error?: { code: string; message: string };
  result?: any;
}

// NWC event kinds (NIP-47)
const NWC_REQUEST_KIND = 23194;
const NWC_RESPONSE_KIND = 23195;

// ---------------------------------------------------------------------------
// Parse nostr+walletconnect:// URI
// ---------------------------------------------------------------------------

export function parseNwcUri(uri: string): NwcConnection {
  // nostr+walletconnect://<walletPubkey>?relay=<relay>&secret=<clientSecretHex>
  const url = new URL(uri);
  const walletPubkey = url.hostname || url.pathname.replace(/^\/\//, "");
  const relay = url.searchParams.get("relay");
  const secret = url.searchParams.get("secret");

  if (!walletPubkey || walletPubkey.length !== 64) {
    throw new Error("Invalid NWC URI: missing or invalid wallet pubkey");
  }
  if (!relay) throw new Error("Invalid NWC URI: missing relay");
  if (!secret || secret.length !== 64) throw new Error("Invalid NWC URI: missing or invalid secret");

  const clientSecret = hexToBytes(secret);
  const clientPubkey = getPublicKey(clientSecret);

  return { walletPubkey, relay, clientSecret, clientPubkey };
}

// ---------------------------------------------------------------------------
// Internal: send NWC request and await response
// ---------------------------------------------------------------------------

async function nwcRequest(conn: NwcConnection, method: string, params: Record<string, any> = {}): Promise<any> {
  const content = JSON.stringify({ method, params });
  const clientSecretHex = bytesToHex(conn.clientSecret);

  // Encrypt with NIP-04 (LNbits NWC provider uses pynostr NIP-04)
  const encrypted = nip04Encrypt(clientSecretHex, conn.walletPubkey, content);

  // Build kind 23194 request event
  const draft: EventTemplate = {
    kind: NWC_REQUEST_KIND,
    created_at: Math.floor(Date.now() / 1000),
    content: encrypted,
    tags: [["p", conn.walletPubkey]],
  };
  const signed = finalizeEvent(draft, conn.clientSecret);

  // Subscribe for kind 23195 response from wallet service referencing our request.
  // IMPORTANT: use subscription() not request() — request() completes on EOSE
  // which fires before the provider has time to respond, causing EmptyError.
  const response$ = pool.subscription([conn.relay], [
    {
      kinds: [NWC_RESPONSE_KIND],
      authors: [conn.walletPubkey],
      "#e": [signed.id],
    },
  ]).pipe(
    filter((event): event is NostrEvent => typeof event === "object" && "kind" in event),
    map((event) => {
      const decrypted = nip04Decrypt(clientSecretHex, conn.walletPubkey, event.content);
      return JSON.parse(decrypted) as NwcResponse;
    }),
    rxTimeout(30_000),
  );

  // Publish the request
  pool.publish([conn.relay], signed);

  // Await first response
  const response = await firstValueFrom(response$);

  if (response.error) {
    throw new Error(`NWC error (${response.error.code}): ${response.error.message}`);
  }

  return response.result;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Get wallet balance in sats */
export async function nwcGetBalance(conn: NwcConnection): Promise<number> {
  const result = await nwcRequest(conn, "get_balance");
  // NWC returns balance in msats
  return Math.floor((result.balance ?? 0) / 1000);
}

/** Pay a bolt11 invoice, returns preimage */
export async function nwcPayInvoice(conn: NwcConnection, bolt11: string): Promise<{ preimage: string }> {
  const start = performance.now();
  const result = await nwcRequest(conn, "pay_invoice", { invoice: bolt11 });
  recordTiming({
    type: "lightning_pay",
    durationMs: performance.now() - start,
    timestamp: Date.now(),
    amount: 0, // amount not easily extractable from bolt11 here
  });
  return { preimage: result.preimage };
}

/** Create an invoice to receive payment */
export async function nwcMakeInvoice(
  conn: NwcConnection,
  amount: number,
  description?: string,
): Promise<{ invoice: string; payment_hash: string }> {
  const start = performance.now();
  // NWC expects amount in msats
  const result = await nwcRequest(conn, "make_invoice", {
    amount: amount * 1000,
    description: description ?? "",
  });
  recordTiming({
    type: "lightning_receive",
    durationMs: performance.now() - start,
    timestamp: Date.now(),
    amount,
  });
  return { invoice: result.invoice, payment_hash: result.payment_hash };
}

/** List recent transactions */
export async function nwcListTransactions(
  conn: NwcConnection,
  opts?: { limit?: number; offset?: number; type?: "incoming" | "outgoing" },
): Promise<NwcTransaction[]> {
  const result = await nwcRequest(conn, "list_transactions", {
    limit: opts?.limit ?? 50,
    offset: opts?.offset ?? 0,
    ...(opts?.type ? { type: opts.type } : {}),
  });
  return result.transactions ?? [];
}

/** Look up a specific invoice by payment hash */
export async function nwcLookupInvoice(conn: NwcConnection, paymentHash: string): Promise<NwcTransaction> {
  return nwcRequest(conn, "lookup_invoice", { payment_hash: paymentHash });
}
